import React, { useState, useEffect } from 'react';
import { 
  Users, 
  X, 
  Trash2, 
  Mail, 
  Phone,
  Search,
  AlertCircle,
  Loader
} from 'lucide-react';
import { db } from '../../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  deleteDoc
} from 'firebase/firestore';
import toast from 'react-hot-toast';

const BatchEnrolledStudents = ({ batchId, batchName, onClose }) => {
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadEnrolledStudents();
  }, [batchId]);

  const loadEnrolledStudents = async () => {
    try {
      setLoading(true);
      
      // Load enrollments for this batch
      const enrollmentsRef = collection(db, 'batchEnrollments');
      const enrollmentsQuery = query(enrollmentsRef, where('batchId', '==', batchId));
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      
      const enrollmentsData = enrollmentsSnapshot.docs.map(doc => ({
        enrollmentId: doc.id,
        ...doc.data()
      }));
      
      setEnrolledStudents(enrollmentsData);
    } catch (error) {
      console.error('Error loading enrolled students:', error);
      toast.error('Failed to load enrolled students');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStudent = async (enrollmentId, studentName) => {
    if (!window.confirm(`Are you sure you want to remove ${studentName} from this batch?`)) {
      return;
    }

    try {
      setDeleting(enrollmentId);
      
      const enrollmentRef = doc(db, 'batchEnrollments', enrollmentId);
      await deleteDoc(enrollmentRef);
      
      toast.success('Student removed from batch');
      loadEnrolledStudents();
    } catch (error) {
      console.error('Error removing student:', error);
      toast.error('Failed to remove student');
    } finally {
      setDeleting(null);
    }
  };

  const filteredStudents = enrolledStudents.filter(student =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Users className="w-6 h-6 mr-2" />
              Enrolled Students
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {batchName} • {enrolledStudents.length} student(s) enrolled
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search Bar */}
        {enrolledStudents.length > 0 && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search students by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        )}

        {/* Students List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredStudents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {enrolledStudents.length === 0 
                  ? 'No students enrolled in this batch yet'
                  : 'No students found matching your search'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredStudents.map((student) => (
                <div
                  key={student.enrollmentId}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white text-lg">
                        {student.name}
                      </h4>
                      <div className="mt-2 space-y-1">
                        {student.email && (
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Mail className="w-4 h-4 mr-2" />
                            {student.email}
                          </div>
                        )}
                        {student.phone && (
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Phone className="w-4 h-4 mr-2" />
                            {student.phone}
                          </div>
                        )}
                      </div>
                      {student.enrolledAt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Enrolled: {new Date(student.enrolledAt.seconds * 1000).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveStudent(student.enrollmentId, student.name)}
                      disabled={deleting === student.enrollmentId}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50"
                      title="Remove from batch"
                    >
                      {deleting === student.enrollmentId ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              Total: {enrolledStudents.length} student(s)
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchEnrolledStudents;
